// apps/web/src/pages/Profile/components/AddressSection.tsx

import { useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { MapPin, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  useCreateMyAddress,
  useDeleteMyAddress,
  useMyAddresses,
  useStates,
  useUpdateMyAddress,
  useCepLookup,
} from '@alamo/api';
import type { ProfileAddress, ProfileAddressType } from '@alamo/types';
import {
  Badge,
  Button,
  ReasonDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
  Spinner,
  cn,
} from '@alamo/ui';
import { extractApiError } from '@alamo/utils';

import {
  GeographicCitySelect,
  GeographicStateSelect,
} from '../../../components/admin/EntitySelects';

const ADDRESS_TYPES: ProfileAddressType[] = ['residential', 'commercial', 'mailing'];

const baseAddressSchema = z.object({
  addressType: z.enum(['residential', 'commercial', 'mailing']),
  zipCode: z.string().optional(),
  street: z.string().max(255).optional(),
  number: z.string().max(20).optional(),
  complement: z.string().max(100).optional(),
  neighborhood: z.string().max(100).optional(),
  // Nulo é um estado de CARREGAMENTO válido: `party_addresses.city_id` aceita
  // NULL para o import preservar CEP/logradouro/número/bairro quando só a cidade
  // não resolve (docs/contracts/ESCR-endereco-city-id-nulavel.md). Exigir cidade
  // aqui travaria a edição de qualquer outro campo do endereço.
  //
  // Sem checagem de formato de propósito: o valor só chega da API ou do seletor,
  // e um `.uuid()` reprovaria em silêncio — o erro renderiza junto do seletor de
  // cidade, que nem aparece quando o endereço já tem vínculo. O formato quem
  // valida é o `@IsUUID()` do backend, cujo 422 vira toast.
  cityId: z.string().nullable(),
  cityName: z.string().optional(),
  ufSnapshot: z.string().optional(),
  country: z.string().optional(),
  isPrimary: z.boolean().optional(),
});

type AddressFormValues = z.infer<typeof baseAddressSchema>;

/**
 * Cidade é regra de SUBMIT, não de carregamento, e só vale para criação:
 * `POST /profile/addresses` valida `cityId` com `@IsUUID()`, enquanto o
 * `PATCH /profile/addresses/:id` trata `cityId` como opcional e preserva o
 * vínculo atual — inclusive NULL — quando o campo não vem no corpo.
 */
function buildAddressSchema(requireCity: boolean) {
  return baseAddressSchema.superRefine((values, ctx) => {
    if (requireCity && !values.cityId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cityId'],
        message: 'auth:profile.addresses.city_required_new',
      });
    }
  });
}

interface AddressDialogProps {
  open: boolean;
  onClose: () => void;
  address?: ProfileAddress;
}

function AddressDialog({ open, onClose, address }: AddressDialogProps) {
  const { t } = useTranslation(['auth', 'common']);
  const create = useCreateMyAddress();
  const update = useUpdateMyAddress();
  const isEditing = !!address;

  const [cepInput, setCepInput] = useState(address?.zipCode ?? '');
  const { data: cepData, isFetching: cepLoading } = useCepLookup(cepInput);

  // Fixo por montagem: o parent remonta o dialog por alvo (prop `key`). Quem
  // abre sem vínculo continua com os seletores à vista depois de escolher a
  // cidade, em vez de ver o bloco sumir sob o cursor.
  const cityLinkedOnOpen = !!address?.cityId;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<AddressFormValues>({
    resolver: zodResolver(buildAddressSchema(!isEditing)),
    defaultValues: address
      ? {
          addressType: address.addressType,
          zipCode: address.zipCode ?? '',
          street: address.street ?? '',
          number: address.number ?? '',
          complement: address.complement ?? '',
          neighborhood: address.neighborhood ?? '',
          cityId: address.cityId,
          // Sem vínculo, o snapshot é justamente o par cidade/UF que não casou
          // com o catálogo e pode estar errado na origem (cidade de um estado
          // com a UF de outro). Semeá-lo filtraria a lista pela UF errada e
          // ofereceria um cityId incorreto como se fosse conferido: a cascata
          // nasce vazia e o snapshot aparece só como informação, no aviso.
          cityName: address.cityId ? address.cityNameSnapshot : '',
          ufSnapshot: address.cityId ? address.ufSnapshot : '',
          country: address.country,
          isPrimary: address.isPrimary,
        }
      : {
          addressType: 'residential',
          zipCode: '',
          street: '',
          number: '',
          complement: '',
          neighborhood: '',
          cityId: null,
          cityName: '',
          ufSnapshot: '',
          country: 'Brasil',
          isPrimary: false,
        },
  });

  const currentType = watch('addressType');
  const selectedUf = watch('ufSnapshot') ?? '';

  // `GeographicStateSelect` trabalha com o UUID do estado; o form guarda a UF
  // (é ela que alimenta a cascata e o snapshot exibido). O catálogo de estados
  // é cache de 24h, então a conversão não custa requisição.
  const { data: states } = useStates();
  const selectedStateId = useMemo(
    () => states?.find((s) => s.uf === selectedUf.toUpperCase())?.id ?? null,
    [states, selectedUf],
  );

  // Auto-fill via CEP lookup
  const handleCepChange = (value: string) => {
    setCepInput(value);
    setValue('zipCode', value);
  };

  useEffect(() => {
    if (cepData && !cepLoading) {
      if (cepData.cityId) {
        setValue('cityId', cepData.cityId);
        setValue('cityName', cepData.cityName ?? '');
        setValue('ufSnapshot', cepData.uf ?? '');
      }
      if (cepData.street) setValue('street', cepData.street);
      if (cepData.neighborhood) setValue('neighborhood', cepData.neighborhood);
    }
  }, [cepData, cepLoading, setValue]);

  const isPending = create.isPending || update.isPending;

  const onSubmit = (values: AddressFormValues) => {
    const payload = {
      addressType: values.addressType,
      zipCode: values.zipCode || undefined,
      street: values.street || undefined,
      number: values.number || undefined,
      complement: values.complement || undefined,
      neighborhood: values.neighborhood || undefined,
      country: values.country || 'Brasil',
      isPrimary: values.isPrimary ?? false,
    };

    const onSuccess = () => {
      toast.success(
        isEditing
          ? t('auth:profile.addresses.toast.updated')
          : t('auth:profile.addresses.toast.created'),
      );
      reset();
      onClose();
    };

    const onError = (err: unknown) => {
      const { message, code } = extractApiError(err);
      toast.error(
        t(`errors:${code ?? 'generic'}`, {
          defaultValue: message ?? t('errors:generic', { defaultValue: 'Erro inesperado' }),
        }),
      );
    };

    if (isEditing && address) {
      // `cityId` só entra no PATCH quando há UUID. Omitido, o backend não toca
      // em `city_id`/`city_name_snapshot`/`uf_snapshot`: o endereço importado
      // sem vínculo continua sem vínculo e o resto da edição passa.
      update.mutate(
        {
          id: address.id,
          input: values.cityId ? { ...payload, cityId: values.cityId } : payload,
        },
        { onSuccess, onError },
      );
      return;
    }

    // Criação exige UUID (`@IsUUID()` no CreateAddressDto); o schema já barra
    // antes daqui, e a guarda estreita o tipo para o DTO.
    if (!values.cityId) return;
    create.mutate({ ...payload, cityId: values.cityId }, { onSuccess, onError });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('auth:profile.addresses.edit') : t('auth:profile.addresses.add')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {/* Tipo */}
          <div>
            <label className="block text-sm font-body font-medium text-slate-700 mb-1">
              {t('auth:profile.addresses.fields.type')}
            </label>
            <div className="flex gap-2 flex-wrap">
              {ADDRESS_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setValue('addressType', type)}
                  className={cn(
                    'px-3 py-2 rounded-md border text-sm font-body transition-colors min-h-[44px]',
                    currentType === type
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {t(`auth:profile.addresses.type_${type}`)}
                </button>
              ))}
            </div>
          </div>

          {/* CEP */}
          <div className="relative">
            <Input
              label={t('auth:profile.addresses.fields.zip')}
              placeholder="00000-000"
              value={cepInput}
              onChange={(e) => handleCepChange(e.target.value)}
            />
            {cepLoading && (
              <div className="absolute right-3 top-9">
                <Spinner size="sm" />
              </div>
            )}
          </div>
          {/* O proxy de CEP responde endereço sem `cityId` quando o município
              não casa com o catálogo: dizer isso evita que o consultor fique
              esperando o preenchimento automático que não vem. */}
          {!cepLoading && cepData && !cepData.cityId && (
            <p className="text-xs text-slate-500">{t('common:location.cep_not_resolved')}</p>
          )}

          {/* Rua */}
          <Input
            label={t('auth:profile.addresses.fields.street')}
            {...register('street')}
            error={errors.street ? t(errors.street.message as string) : undefined}
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              label={t('auth:profile.addresses.fields.number')}
              {...register('number')}
              error={errors.number ? t(errors.number.message as string) : undefined}
            />
            <Input
              label={t('auth:profile.addresses.fields.complement')}
              {...register('complement')}
            />
          </div>

          <Input
            label={t('auth:profile.addresses.fields.neighborhood')}
            {...register('neighborhood')}
          />

          {/* Cidade — dois modos.
              Vinculada: snapshot read-only, mantido pela consulta de CEP.
              Sem vínculo (city_id NULL herdado do import, ou endereço novo): a
              cascata UF→cidade, porque o CEP sozinho pode não resolver e sem
              outra porta o consultor ficaria sem NENHUMA forma de informar a
              cidade num campo que não consegue digitar. */}
          {cityLinkedOnOpen ? (
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Input
                  label={t('auth:profile.addresses.fields.city')}
                  value={watch('cityName') ?? ''}
                  readOnly
                  disabled
                />
              </div>
              <Input
                label={t('auth:profile.addresses.fields.state')}
                value={watch('ufSnapshot') ?? ''}
                readOnly
                disabled
              />
            </div>
          ) : (
            <div className="space-y-3">
              {isEditing && address && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                  {t('auth:profile.addresses.city_unresolved', {
                    city: address.cityNameSnapshot,
                    uf: address.ufSnapshot,
                  })}
                </p>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <GeographicStateSelect
                  label={t('auth:profile.addresses.fields.state')}
                  value={selectedStateId}
                  onChange={(_, state) => {
                    // Trocar a UF invalida a cidade: o UUID antigo é de outro estado.
                    setValue('ufSnapshot', state?.uf ?? '');
                    setValue('cityId', null);
                    setValue('cityName', '');
                  }}
                  required={!isEditing}
                />
                <GeographicCitySelect
                  label={t('auth:profile.addresses.fields.city')}
                  stateUf={selectedUf || null}
                  value={watch('cityId')}
                  onChange={(id, city) => {
                    setValue('cityId', id, { shouldValidate: true });
                    setValue('cityName', city?.name ?? '');
                    if (city?.state?.uf) setValue('ufSnapshot', city.state.uf);
                  }}
                  required={!isEditing}
                  error={errors.cityId ? t(errors.cityId.message as string) : undefined}
                />
              </div>
            </div>
          )}

          {/* Principal */}
          <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
            <input
              type="checkbox"
              className="size-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
              {...register('isPrimary')}
            />
            <span className="text-sm font-body text-slate-700">
              {t('auth:profile.addresses.fields.is_primary')}
            </span>
          </label>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isPending}
              aria-busy={isPending || undefined}
            >
              {isPending && <Spinner size="sm" />}
              {t('common:actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AddressSection() {
  const { t } = useTranslation(['auth', 'common']);
  const { data: addresses, isLoading } = useMyAddresses();
  const deleteAddress = useDeleteMyAddress();
  const updateAddress = useUpdateMyAddress();

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    address?: ProfileAddress;
  }>({ open: false });
  const [deleteTarget, setDeleteTarget] = useState<ProfileAddress | null>(null);

  const handleSetPrimary = (address: ProfileAddress) => {
    if (address.isPrimary) return;
    updateAddress.mutate(
      { id: address.id, input: { isPrimary: true } },
      {
        onSuccess: () => toast.success(t('auth:profile.addresses.toast.updated')),
        onError: (err) => {
          const { message } = extractApiError(err);
          toast.error(message ?? t('errors:generic', { defaultValue: 'Erro inesperado' }));
        },
      },
    );
  };

  const handleDelete = ({ reason }: { reason: string }) => {
    if (!deleteTarget) return;
    deleteAddress.mutate(
      { id: deleteTarget.id, reason },
      {
        onSuccess: () => {
          toast.success(t('auth:profile.addresses.toast.deleted'));
          setDeleteTarget(null);
        },
        onError: (err) => {
          const { message } = extractApiError(err);
          toast.error(message ?? t('errors:generic', { defaultValue: 'Erro inesperado' }));
          setDeleteTarget(null);
        },
      },
    );
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-heading font-bold text-secondary-950">
          {t('auth:profile.sections.addresses')}
        </h2>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setDialogState({ open: true })}
          className="min-h-[44px]"
        >
          <Plus className="size-4" aria-hidden />
          {t('auth:profile.addresses.add')}
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : !addresses || addresses.length === 0 ? (
        <p className="text-sm text-slate-500">{t('auth:profile.addresses.empty')}</p>
      ) : (
        <ul className="divide-y divide-slate-100 space-y-0">
          {addresses.map((addr) => (
            <li key={addr.id} className="py-3 flex items-start gap-3">
              <MapPin className="size-4 text-slate-400 mt-0.5 shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-body font-medium text-secondary-950">
                    {t(`auth:profile.addresses.type_${addr.addressType}`)}
                  </span>
                  {addr.isPrimary && (
                    <Badge variant="info" className="text-xs">
                      {t('auth:profile.addresses.primary_badge')}
                    </Badge>
                  )}
                  {/* Sem o selo, o endereço parece completo na listagem: o
                      snapshot de cidade/UF continua preenchido mesmo sem
                      vínculo com o catálogo. */}
                  {!addr.cityId && (
                    <Badge variant="warning" className="text-xs">
                      {t('auth:profile.addresses.city_unlinked_badge')}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-600 mt-0.5 break-words">
                  {[addr.street, addr.number, addr.complement].filter(Boolean).join(', ')}
                </p>
                <p className="text-xs text-slate-500">
                  {[addr.neighborhood, addr.cityNameSnapshot, addr.ufSnapshot]
                    .filter(Boolean)
                    .join(' · ')}
                  {addr.zipCode ? ` — ${addr.zipCode}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!addr.isPrimary && (
                  <button
                    type="button"
                    title={t('auth:profile.addresses.set_primary')}
                    onClick={() => handleSetPrimary(addr)}
                    disabled={updateAddress.isPending}
                    className="p-2 rounded-md text-slate-400 hover:text-primary-600 hover:bg-slate-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  >
                    <Star className="size-4" aria-hidden />
                    <span className="sr-only">{t('auth:profile.addresses.set_primary')}</span>
                  </button>
                )}
                <button
                  type="button"
                  title={t('auth:profile.addresses.edit')}
                  onClick={() => setDialogState({ open: true, address: addr })}
                  className="p-2 rounded-md text-slate-400 hover:text-secondary-700 hover:bg-slate-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                >
                  <Pencil className="size-4" aria-hidden />
                  <span className="sr-only">{t('auth:profile.addresses.edit')}</span>
                </button>
                <button
                  type="button"
                  title={t('auth:profile.addresses.delete')}
                  onClick={() => setDeleteTarget(addr)}
                  className="p-2 rounded-md text-slate-400 hover:text-error-600 hover:bg-red-50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                >
                  <Trash2 className="size-4" aria-hidden />
                  <span className="sr-only">{t('auth:profile.addresses.delete')}</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <AddressDialog
        // key remonta o dialog por alvo: useForm captura defaultValues na
        // MONTAGEM — sem isso, editar abre com os campos vazios/stale.
        key={dialogState.address?.id ?? 'new'}
        open={dialogState.open}
        onClose={() => setDialogState({ open: false })}
        address={dialogState.address}
      />

      <ReasonDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title={t('auth:profile.addresses.delete')}
        description={t('auth:profile.addresses.delete_confirm')}
        reasonLabel={t('common:deletion.reason_label')}
        reasonPlaceholder={t('common:deletion.reason_placeholder')}
        minChars={10}
        maxChars={500}
        confirmLabel={t('common:actions.delete')}
        confirmVariant="danger"
        onSubmit={handleDelete}
        isSubmitting={deleteAddress.isPending}
      />
    </section>
  );
}
